import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import FileView from '@plone/volto/components/theme/View/FileView';
import ImageView from '@plone/volto/components/theme/View/ImageView';
import { CONTENT_TYPES } from '../../types/download';
import type {
  DownloadRedirectViewProps,
  PrevRouteState,
  VoltoSettings,
} from '../../types/download';

const API_SUFFIX = '/++api++';
const INLINE_FILE_ENDPOINT = '@@inline-file/file';
const DOWNLOAD_ENDPOINT = '@@download';
const CONTENT_TYPE_PDF = 'application/pdf';
const isClient = typeof window !== 'undefined';

const getApiBase = (): string => {
  const settings = config.settings as VoltoSettings;
  let api = String(settings?.apiPath || '').trim();

  if (!api) {
    if (isClient && window.location?.origin) {
      return `${window.location.origin}${API_SUFFIX}`;
    }
    return API_SUFFIX;
  }

  if (api.includes(API_SUFFIX)) return api;

  return api.replace(/\/+$/, '') + API_SUFFIX;
};

const ensureLeadingSlash = (path: string): string => {
  return path.startsWith('/') ? path : `/${path}`;
};

const buildInlinePdfUrl = (downloadUrl: string): string => {
  const base = getApiBase();
  const origin = isClient ? window.location.origin : 'http://localhost';
  const u = new URL(downloadUrl, origin);

  const sitePath = u.pathname
    .replace(/^\/\+\+api\+\+/, '')
    .replace(/^\/Plone/, '');

  const baseDoc = sitePath.split(`/${DOWNLOAD_ENDPOINT}/`)[0];
  return `${base}${ensureLeadingSlash(baseDoc)}/${INLINE_FILE_ENDPOINT}`;
};

const hasValidLocation = (): boolean => {
  return isClient && 'location' in window && window.location !== null;
};

const performRedirect = (url: string): void => {
  if (typeof window.location.replace === 'function') {
    window.location.replace(url);
  } else if (typeof window.location.assign === 'function') {
    window.location.assign(url);
  }
};

const DownloadRedirectView = ({
  token,
  content,
}: DownloadRedirectViewProps) => {
  const prev = useSelector((s: PrevRouteState) => s.prevRoute?.prev);

  const file = content?.file;
  const image = content?.image;

  let targetUrl: string | null = null;

  if (content['@type'] === CONTENT_TYPES.Image && content?.link) {
    targetUrl = flattenToAppURL(content.link);
  } else if (file?.download) {
    if (file['content-type'] === CONTENT_TYPE_PDF) {
      targetUrl = buildInlinePdfUrl(file.download);
    } else {
      targetUrl = flattenToAppURL(file.download);
    }
  } else if (image?.download) {
    targetUrl = flattenToAppURL(image.download);
  }

  useEffect(() => {
    // Do not redirect if user is logged in
    if (typeof token !== 'undefined') return;
    if (!isClient || !content) return;
    if (!hasValidLocation()) return;

    const searchParams = prev ? new URLSearchParams(prev.search) : null;
    const cameFromAdd =
      !!prev &&
      prev.pathname.endsWith('/add') &&
      searchParams?.has('type') &&
      /^(File|Image)$/.test(searchParams.get('type') || '');

    if (
      cameFromAdd &&
      (content['@type'] === CONTENT_TYPES.File ||
        content['@type'] === CONTENT_TYPES.Image)
    ) {
      const appUrl = flattenToAppURL(content['@id'] || '');
      const url = new URL(appUrl, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      parts.pop();
      performRedirect(`/${parts.join('/')}/contents`);
      return;
    }

    if (!targetUrl) return;

    performRedirect(targetUrl);
  }, [content, prev, token, targetUrl]);

  if (!isClient && !token && targetUrl) {
    return <Redirect to={targetUrl} />;
  }

  // Use default views if user is logged in
  if (typeof token !== 'undefined') {
    if (content['@type'] === CONTENT_TYPES.File) {
      return <FileView token={token} content={content} />;
    } else if (content['@type'] === CONTENT_TYPES.Image) {
      return <ImageView token={token} content={content} />;
    }
  }

  return null;
};

export default DownloadRedirectView;
